import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {fromEvent} from 'rxjs';
import {CdkDragDrop, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {debounceTime, map} from "rxjs/operators";
import {Compbaser} from "../compbaser/compbases";
import * as _ from 'lodash';
import {MAX_PLAYLIST_TIME} from '../../common/global-constants';
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";

/**
 *
 * example usage:
 *     <app-tables-selector [setConfig]="tablesSelectorInput" [displayedColumnsSource]="fields" placeholder="{{'placeholder.filterList' | transloco}}" [displayedColumnsDestination]="fields" (onChange)="onPlaylistChanged($event)">
 *     </app-tables-selector>
 */

export interface ITablesSelector {
    name: string;
    updatedValue?: any;
    __TableId?: string; // private, filled by filled by component
}

export interface ITablesSelectorSet {
    source: Array<ITablesSelector>;
    destination: Array<ITablesSelector>;
    editType?: string;
}

@Component({
    selector: 'app-tables-selector',
    templateUrl: './tables-selector.component.html',
    styleUrls: ['./tables-selector.component.scss'],
    standalone: false
})
export class TablesSelectorComponent extends Compbaser implements AfterViewInit {
    items: Array<ITablesSelector>;
    itemsFiltered: Array<ITablesSelector>;
    destination: Array<ITablesSelector> = [];
    valueInput;
    selectedRowItem;
    displayedColumnsDestinationNoAction;
    displayedColumnsDestinationAll;
    displayedColumnsSourceNoDragIcon;
    displayedColumnsSourceAll;
    maxStringSize = '22';

    @ViewChild('dtable') dtable: any;
    @ViewChild('search') search: any;

    @Input() set displayedColumnsSource(i_value) {
        this.displayedColumnsSourceNoDragIcon = i_value;
        this.displayedColumnsSourceAll = ['drag', ...i_value];
    }

    @Input() placeholder = '';

    @Input() set displayedColumnsDestination(i_value) {
        this.displayedColumnsDestinationNoAction = i_value;
        this.displayedColumnsDestinationAll = [...i_value, 'action'];
    }

    @Input() set setConfig(i_tablesSelectorSet: ITablesSelectorSet) {
        i_tablesSelectorSet = this.injectIds(i_tablesSelectorSet);
        if (!i_tablesSelectorSet) return;
        this.items = i_tablesSelectorSet.source;
        this.itemsFiltered = [...this.items];
        this.destination = i_tablesSelectorSet.destination;
    }

    @Output()
    onChange: EventEmitter<ITablesSelectorSet> = new EventEmitter();

    constructor(private cd: ChangeDetectorRef, private toastr: ToastrService, private translocoService: TranslocoService) {
        super();
    }

    private injectIds(i_value: ITablesSelectorSet): ITablesSelectorSet {
        i_value.source.forEach((item: ITablesSelector) => item.__TableId = _.uniqueId());
        i_value.destination.forEach((item: ITablesSelector) => item.__TableId = _.uniqueId());
        return i_value;
    }

    ngAfterViewInit(): void {
        this.cod(
            fromEvent(this.search.nativeElement, 'keydown')
                .pipe(
                    debounceTime(550),
                    map(x => x['target']['value'])
                )
                .subscribe((value) => {
                    this.updateFilter(value);
                    this.cd.markForCheck();
                })
        );
    }

    updateFilter(val: any) {
        this.itemsFiltered = [];
        this.itemsFiltered = this.items.filter((item: ITablesSelector) => {
            for (let i = 0; i < this.items.length; i++) {
                return !!item.name.toLocaleLowerCase().trim().match(val.toLocaleLowerCase().trim());
            }
        });
    }

    reset() {
        this.valueInput = '';
        this.itemsFiltered = [...this.items];
        this.search.nativeElement.value = '';
        this.dtable.renderRows();
    }

    removeItem(a, b) {
        this.destination = this.destination.filter((c) => {
            return b.__TableId !== c.__TableId;
        });
        this.onChange.emit({source: this.items, destination: this.destination});
    }

    selectedRow(row) {
        this.selectedRowItem = row;
    }

    selectedRowDestination(row) {
        // console.log(row);
    }

    // drop(event: CdkDragDrop<string[]>) {
    drop(event: CdkDragDrop<Array<ITablesSelector>, any>) {
        if (event.previousContainer === event.container) {
            const idx = this.destination.findIndex((v) => v === event.item.data);
            if (idx == -1) {
                return;
            }
            moveItemInArray(event.container.data, idx, event.currentIndex);
        } else {
            // copyArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
            let totalLength = this.getDestTotalLength();
            if (this.selectedRowItem['duration']) {
                totalLength += +this.selectedRowItem['duration'];
            }
            if (totalLength > MAX_PLAYLIST_TIME) {
                this.showToastOverLength();
                return;
            }
            const idx = this.itemsFiltered.findIndex((v) => v === this.selectedRowItem);

            transferArrayItem(event.previousContainer.data, event.container.data, idx, event.currentIndex);
        }
        const o = Object.assign({}, this.destination[event.currentIndex]);
        if (!o.__TableId) return;
        this.destination[event.currentIndex] = Object.assign({}, this.destination[event.currentIndex]);
        this.destination[event.currentIndex].__TableId = String(Math.random());
        this.itemsFiltered = [...this.items];
        this.updateFilter(this.search.nativeElement.value);
        this.dtable.renderRows();
        this.onChange.emit({source: this.items, destination: this.destination});
    }

    updateField(event, col: string, field: ITablesSelector) {
        this.destination.forEach((item) => {
            if (item.__TableId === field.__TableId) {
                if (col == 'name' && event) {
                    if (event) {
                        item.name = event;
                    }
                } else {
                    const diffDuration = parseFloat(event) - parseFloat(item['duration']);
                    if (this.getDestTotalLength() + diffDuration > MAX_PLAYLIST_TIME) {
                        this.showToastOverLength();
                        return;
                    }
                    item.updatedValue = event;
                }
            }
        });
        this.onChange.emit({source: this.items, destination: this.destination, editType: col});
    }

    private getDestTotalLength() {
        let len = 0;
        this.destination.forEach((_dest) => {
            if (_dest['duration']) {
                len += parseFloat(_dest['duration']);
            }
        });
        return len;
    }

    private showToastOverLength() {
        this.toastr.error(this.translocoService.translate('general.tooLong24', {}, this.translocoService.getActiveLang()));
    }
}

// this.search.nativeElement.oncut = this.search.nativeElement.oncopy = this.search.nativeElement.onpaste = (event) => {
// this.reset();
// };
// map((v: KeyboardEvent) => {
// console.log(v.code);
// if (v.code === 'Backspace' || v.code === 'Delete')
//   this.reset();
// return v;
// }),
//
// @HostListener('window:resize', ['$event'])
// onResize(event) {
//   this.view = [event.target.innerWidth / 1.35, 400];
// }
